import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import { groupAdminApiSlice } from '../group/groupAdminApiSlice';
import { occupancyApiSlice } from '../group/occupancyApiSlice';
import {
    BulkAddOrganizationMembersArgs,
    BulkResult,
    CancelSpaceAgreementArgs,
    CreateOrganizationArgs,
    CreateOrganizationUnitArgs,
    CreateSpaceAgreementArgs,
    CustomerClass,
    DeleteOrganizationUnitArgs,
    ListOrganizationMembersArgs,
    ListOrganizationRoomAccessArgs,
    ListOrganizationsArgs,
    ListSpaceAgreementsArgs,
    MemberRow,
    Organization,
    OrganizationDetail,
    OrganizationListResponse,
    OrganizationRoomAccessResponse,
    OrganizationUnit,
    RemoveOrganizationMemberArgs,
    SetMembershipUnitArgs,
    SetOrganizationPrimaryContactArgs,
    SetOrganizationRoomAccessArgs,
    SpaceAgreement,
    SpaceAgreementListResponse,
    UpdateOrganizationArgs,
    UpdateOrganizationUnitArgs,
    UpdateSpaceAgreementArgs,
} from './organizationApiTypes';
import {
    CopySeasonArgs,
    CopySeasonReport,
    GrantWindowsArgs,
    GrantWindowsPreview,
    GrantWindowsResponse,
    PlanImportArgs,
    PlanImportReport,
    SaveGrantWindowsArgs,
    Season,
    TenantWindow,
    TenantWindowsArgs,
    UtilisationReport,
    WindowReleaseArgs,
    WindowReleaseResult,
} from './allocationWindowTypes';
import type {
    OrganizationImportArgs,
    OrganizationImportFormat,
    OrganizationImportReport,
    OrganizationImportResult,
    SendOrganizationImportInvitesResult,
} from './organizationImportTypes';

/**
 * The organisation import's multipart body: the file text, its format and,
 * for a CSV, the page default type. Multipart like the onboarding import, so
 * the file is not held to the 100 kB JSON limit (the route reads 2 MB).
 */
const organizationImportForm = (args: OrganizationImportArgs): FormData => {
    const form = new FormData();
    form.append('file', args.fileText);
    form.append('format', args.format);
    if (args.defaultType) form.append('defaultType', args.defaultType);
    return form;
};

/**
 * Customer-organization CRUD slice (H-3b).
 *
 * Tenant scoping is enforced server-side: `newBackendAxiosBaseQuery` routes
 * through `sendNewBackendApiRequest`, which injects the `X-Tenant` header
 * automatically. All list endpoints therefore return tenant-scoped data only.
 *
 * The REST endpoints (plan §5) do not exist yet — this is a
 * frontend-behind-contract slice. A runtime 404 is expected until the Lead
 * Developer ships the backend.
 *
 * Tag discipline (house rule): every `invalidatesTags` below references a tag
 * that at least one query `provides`.
 *   - organizationList   ← provided by getOrganizations
 *   - organizationDetail  ← provided by getOrganization
 *   - customerClassList   ← provided by getCustomerClasses
 *   - organizationMemberList ← provided by getOrganizationMembers
 *   - organizationRoomAccess ← provided by getOrganizationRoomAccess
 *   - spaceAgreementList   ← provided by getOrganizationSpaceAgreements
 *   - allocationWindows    ← provided by getGrantWindows and getTenantWindows
 *     (one tag: a save on one grant, a release or a season copy changes what
 *     both the editor and the allocations tab show)
 */
/**
 * The operator week calendar draws the windows from the occupancy read
 * (`occupancyApiSlice`), which tags do not reach across slices. Every window
 * write evicts it, all rooms: a window on a whole hall shows on its parts too.
 */
const evictOccupancy = async (
    _args: unknown,
    { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown, queryFulfilled: Promise<unknown> },
) => {
    try {
        await queryFulfilled;
        dispatch(occupancyApiSlice.util.invalidateTags(['GroupOccupancy']));
    } catch {
        // The screen shows the error; nothing was written.
    }
};

export const organizationApiSlice = createApi({
    reducerPath: 'organizationApi',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    tagTypes: [
        'organizationList',
        'organizationDetail',
        'customerClassList',
        'organizationMemberList',
        'organizationRoomAccess',
        'spaceAgreementList',
        'allocationWindows',
    ],
    endpoints: (builder) => ({
        getOrganizations: builder.query<OrganizationListResponse, ListOrganizationsArgs | void>({
            query: (args) => ({
                url: '/organizations',
                method: 'GET',
                params: args
                    ? {
                        ...(args.search ? { search: args.search } : {}),
                        ...(args.page !== undefined ? { page: args.page } : {}),
                        ...(args.pageSize !== undefined ? { pageSize: args.pageSize } : {}),
                    }
                    : undefined,
            }),
            providesTags: ['organizationList'],
        }),
        getOrganization: builder.query<OrganizationDetail, { id: string }>({
            query: ({ id }) => ({
                url: `/organizations/${id}`,
                method: 'GET',
            }),
            providesTags: ['organizationDetail'],
        }),
        createOrganization: builder.mutation<Organization, CreateOrganizationArgs>({
            query: ({ body }) => ({
                url: '/organizations',
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['organizationList'],
        }),
        updateOrganization: builder.mutation<Organization, UpdateOrganizationArgs>({
            query: ({ id, body }) => ({
                url: `/organizations/${id}`,
                method: 'PUT',
                data: body,
            }),
            invalidatesTags: ['organizationList', 'organizationDetail', 'organizationMemberList'],
        }),
        // Soft-delete (archive). Backend sets `archived_at`; the org drops out
        // of the list but historical bookings are preserved (plan §4.1, §8).
        deleteOrganization: builder.mutation<void, { id: string }>({
            query: ({ id }) => ({
                url: `/organizations/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['organizationList', 'organizationDetail'],
        }),
        createOrganizationUnit: builder.mutation<OrganizationUnit, CreateOrganizationUnitArgs>({
            query: ({ organizationId, body }) => ({
                url: `/organizations/${organizationId}/units`,
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['organizationDetail'],
        }),
        updateOrganizationUnit: builder.mutation<OrganizationUnit, UpdateOrganizationUnitArgs>({
            query: ({ organizationId, unitId, body }) => ({
                url: `/organizations/${organizationId}/units/${unitId}`,
                method: 'PUT',
                data: body,
            }),
            invalidatesTags: ['organizationDetail'],
        }),
        deleteOrganizationUnit: builder.mutation<void, DeleteOrganizationUnitArgs>({
            query: ({ organizationId, unitId }) => ({
                url: `/organizations/${organizationId}/units/${unitId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['organizationDetail'],
        }),
        // Unified member list (plan §4.3a): open invites + claimed
        // memberships as one MemberRow[] — the storage split is invisible.
        getOrganizationMembers: builder.query<MemberRow[], ListOrganizationMembersArgs>({
            query: ({ organizationId }) => ({
                url: `/organizations/${organizationId}/members`,
                method: 'GET',
            }),
            providesTags: ['organizationMemberList'],
        }),
        // The CSV path: idempotent per e-mail, one invalid address never
        // fails the batch; returns a per-e-mail outcome list.
        bulkAddOrganizationMembers: builder.mutation<BulkResult[], BulkAddOrganizationMembersArgs>({
            query: ({ organizationId, body }) => ({
                url: `/organizations/${organizationId}/members/bulk`,
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['organizationMemberList'],
        }),
        // Offboarding in one call: archives the open invite if unclaimed,
        // suspends the membership if claimed. The account itself is untouched.
        removeOrganizationMember: builder.mutation<void, RemoveOrganizationMemberArgs>({
            query: ({ organizationId, email }) => ({
                url: `/organizations/${organizationId}/members/${encodeURIComponent(email)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['organizationMemberList'],
        }),
        /**
         * Designate (or clear) the organization's primary contact (#2163).
         *
         * Writes the `owner` role through the existing membership endpoint —
         * no new route and no new permission: `customers:write` already
         * governs member on-/offboarding, and a new permission would have to
         * be granted in every environment before the button worked for
         * anybody (#2125).
         *
         * Several contacts are allowed (#3037): the backend changes this
         * member only and demotes nobody. `organizationList` is invalidated
         * as well because the Primary contact column derives from this.
         */
        setOrganizationPrimaryContact: builder.mutation<
            unknown, SetOrganizationPrimaryContactArgs
        >({
            query: ({ membershipId, isPrimaryContact }) => ({
                url: `/memberships/${membershipId}`,
                method: 'PUT',
                data: { role: isPrimaryContact ? 'owner' : 'member' },
            }),
            invalidatesTags: ['organizationMemberList', 'organizationList', 'organizationDetail'],
        }),
        /**
         * The unit a member books for (#1810): a covered booking is attributed
         * to it and starts with its default cost centre, and its Leitweg-ID
         * routes the invoice. The backend refuses a unit of another
         * organisation or an archived one; `null` clears it.
         */
        setMembershipUnit: builder.mutation<unknown, SetMembershipUnitArgs>({
            query: ({ membershipId, unitId }) => ({
                url: `/memberships/${membershipId}`,
                method: 'PUT',
                data: { unitId },
            }),
            invalidatesTags: ['organizationMemberList'],
        }),
        // #2144 / ADR-0016 Amendment 5. Read-only in v1: which rooms this
        // organization may use and on what footing (shared vs dedicated).
        // Writes — granting a room, dedicating one, signing an agreement — are
        // owner-gated and land with #2154.
        getOrganizationRoomAccess: builder.query<
            OrganizationRoomAccessResponse, ListOrganizationRoomAccessArgs
        >({
            query: ({ organizationId }) => ({
                url: `/organizations/${organizationId}/room-access`,
                method: 'GET',
            }),
            providesTags: ['organizationRoomAccess'],
        }),
        // Option C: rooms are assigned on the organization. The whole list is
        // replaced; changing a room's contract link is owner-only on the API.
        // Invalidates the agreement list too: its `roomCount` is the rooms each
        // contract covers.
        setOrganizationRoomAccess: builder.mutation<OrganizationRoomAccessResponse, SetOrganizationRoomAccessArgs>({
            query: ({ organizationId, grants }) => ({
                url: `/organizations/${organizationId}/room-access`,
                method: 'PUT',
                data: { grants },
            }),
            invalidatesTags: ['organizationRoomAccess', 'spaceAgreementList'],
            // Open/closed is one fact per room (ADR-0008 amendment 2026-10-13):
            // this save can open or close a room for other organizations too,
            // and the room page and the dialog's warning read it through the
            // group slice's `GroupAccess`. Tags do not cross slices, so it is
            // evicted here — every room, since the save may touch rooms the
            // body no longer names.
            async onQueryStarted(_args, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(groupAdminApiSlice.util.invalidateTags(['GroupAccess']));
                } catch {
                    // The dialog shows the error; nothing was written.
                }
            },
        }),
        // #2165 §3 — record a contract. Both writes are owner-gated on the API
        // (#2154 item 1); a manager gets 403 and the dialog surfaces it.
        //
        // Both invalidate `organizationRoomAccess` as well as the agreement
        // list: a grant row carries `spaceAgreementId`, and the tab's
        // needs-attention banner is counted from the two together — refreshing
        // one and not the other is how the banner disagrees with the rows
        // beside it.
        createSpaceAgreement: builder.mutation<SpaceAgreement, CreateSpaceAgreementArgs>({
            query: ({ organizationId, body }) => ({
                url: `/organizations/${organizationId}/space-agreements`,
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['spaceAgreementList', 'organizationRoomAccess'],
        }),
        updateSpaceAgreement: builder.mutation<SpaceAgreement, UpdateSpaceAgreementArgs>({
            query: ({ agreementId, body }) => ({
                url: `/space-agreements/${agreementId}`,
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: ['spaceAgreementList', 'organizationRoomAccess'],
        }),
        // #2165 §4.1 — give notice, with an end date. A POST of its own rather
        // than a PATCH of `expiryDate`: cancelling has rules (date required,
        // today allowed, past refused) that writing the column directly skips.
        // Invalidates the room access too: the agreement's held rooms are what
        // the warning on the tab is counted from.
        cancelSpaceAgreement: builder.mutation<SpaceAgreement, CancelSpaceAgreementArgs>({
            query: ({ agreementId, endDate }) => ({
                url: `/space-agreements/${agreementId}/cancel`,
                method: 'POST',
                data: { endDate },
            }),
            invalidatesTags: ['spaceAgreementList', 'organizationRoomAccess'],
        }),
        getOrganizationSpaceAgreements: builder.query<
            SpaceAgreementListResponse, ListSpaceAgreementsArgs
        >({
            query: ({ organizationId, includeArchived }) => ({
                url: `/organizations/${organizationId}/space-agreements`,
                method: 'GET',
                params: includeArchived ? { includeArchived: true } : undefined,
            }),
            providesTags: ['spaceAgreementList'],
        }),
        getCustomerClasses: builder.query<CustomerClass[], void>({
            query: () => ({
                url: '/customer-classes',
                method: 'GET',
            }),
            providesTags: ['customerClassList'],
        }),
        // #1808. Until these existed every tenant had exactly one class, the
        // seeded 'Standard', because nothing could create a second — so the
        // tariff axis had a single value to point at. The backend CRUD shipped
        // with H-3b; only the UI half was deferred.
        createCustomerClass: builder.mutation<CustomerClass, { name: string }>({
            query: (body) => ({
                url: '/customer-classes',
                method: 'POST',
                data: body,
            }),
            invalidatesTags: ['customerClassList'],
        }),
        updateCustomerClass: builder.mutation<CustomerClass, { id: string, name: string }>({
            query: ({ id, name }) => ({
                url: `/customer-classes/${id}`,
                method: 'PUT',
                data: { name },
            }),
            // Also the organization lists, which render the class NAME: a
            // rename has to reach every surface showing it, not just this tab.
            invalidatesTags: ['customerClassList', 'organizationList', 'organizationDetail'],
        }),
        archiveCustomerClass: builder.mutation<void, string>({
            query: (id) => ({
                url: `/customer-classes/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['customerClassList', 'organizationList', 'organizationDetail'],
        }),
        // Allocation windows (ADR-0027, M4/M11).
        getGrantWindows: builder.query<GrantWindowsResponse, GrantWindowsArgs>({
            query: ({ organizationId, grantId }) => ({
                url: `/organizations/${organizationId}/room-access/${grantId}/windows`,
                method: 'GET',
            }),
            providesTags: ['allocationWindows'],
        }),
        // A read with a body: what a save would do. A mutation so it runs on demand only.
        previewGrantWindows: builder.mutation<GrantWindowsPreview, SaveGrantWindowsArgs>({
            query: ({ organizationId, grantId, windows }) => ({
                url: `/organizations/${organizationId}/room-access/${grantId}/windows/preview`,
                method: 'POST',
                data: { windows },
            }),
        }),
        saveGrantWindows: builder.mutation<GrantWindowsResponse, SaveGrantWindowsArgs>({
            query: ({ organizationId, grantId, windows, dropReleasedDates }) => ({
                url: `/organizations/${organizationId}/room-access/${grantId}/windows`,
                method: 'PUT',
                data: { windows, dropReleasedDates: dropReleasedDates === true },
            }),
            invalidatesTags: ['allocationWindows'],
            onQueryStarted: evictOccupancy,
        }),
        releaseWindowDate: builder.mutation<WindowReleaseResult, WindowReleaseArgs>({
            query: ({ organizationId, grantId, windowId, date, reason }) => ({
                url: `/organizations/${organizationId}/room-access/${grantId}/windows/${windowId}/releases`,
                method: 'POST',
                data: { date, ...(reason ? { reason } : {}) },
            }),
            invalidatesTags: ['allocationWindows'],
            onQueryStarted: evictOccupancy,
        }),
        restoreWindowDate: builder.mutation<WindowReleaseResult, WindowReleaseArgs>({
            query: ({ organizationId, grantId, windowId, date }) => ({
                url: `/organizations/${organizationId}/room-access/${grantId}/windows/${windowId}/releases/${date}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['allocationWindows'],
            onQueryStarted: evictOccupancy,
        }),
        getTenantWindows: builder.query<{ items: TenantWindow[] }, TenantWindowsArgs | void>({
            query: (args) => ({
                url: '/allocation-windows',
                method: 'GET',
                params: args
                    ? Object.fromEntries(Object.entries(args).filter(([, value]) => typeof value === 'string' && value !== ''))
                    : undefined,
            }),
            providesTags: ['allocationWindows'],
        }),
        // H-14 (#1811): the utilisation report the allocations tab exports as CSV.
        getUtilisation: builder.query<UtilisationReport, Season>({
            query: ({ from, to }) => ({ url: '/allocation-windows/utilisation', method: 'GET', params: { from, to } }),
            providesTags: ['allocationWindows'],
        }),
        previewCopySeason: builder.mutation<CopySeasonReport, CopySeasonArgs>({
            query: (data) => ({ url: '/allocation-windows/copy-season/preview', method: 'POST', data }),
        }),
        copySeason: builder.mutation<CopySeasonReport, CopySeasonArgs>({
            query: (data) => ({ url: '/allocation-windows/copy-season', method: 'POST', data }),
            invalidatesTags: ['allocationWindows'],
            onQueryStarted: evictOccupancy,
        }),
        // The plan import (W2-3): a preview, then all or nothing. It can create
        // room access, so the organisations' room lists are refreshed too.
        previewPlanImport: builder.mutation<PlanImportReport, PlanImportArgs>({
            query: (data) => ({ url: '/allocation-windows/import/preview', method: 'POST', data }),
        }),
        importPlan: builder.mutation<PlanImportReport, PlanImportArgs>({
            query: (data) => ({ url: '/allocation-windows/import', method: 'POST', data }),
            invalidatesTags: ['allocationWindows', 'organizationRoomAccess'],
            onQueryStarted: evictOccupancy,
        }),
        // "Organisationen importieren" (docs/plans/organization-import.md §5.3): the
        // format, a preview (writes nothing), the apply and "Einladungen senden".
        // The base query ends in /admin, so the paths start at /organizations.
        getOrganizationImportFormat: builder.query<OrganizationImportFormat, void>({
            query: () => ({ url: '/organizations/import/format', method: 'GET' }),
        }),
        previewOrganizationImport: builder.mutation<OrganizationImportReport, OrganizationImportArgs>({
            query: (args) => ({ url: '/organizations/import/preview', method: 'POST', data: organizationImportForm(args) }),
        }),
        // The apply writes organisations, classes, units, contacts, contracts,
        // room access and windows: every list showing one of them is evicted.
        applyOrganizationImport: builder.mutation<OrganizationImportResult, OrganizationImportArgs>({
            query: (args) => ({ url: '/organizations/import', method: 'POST', data: organizationImportForm(args) }),
            invalidatesTags: [
                'organizationList', 'organizationDetail', 'customerClassList', 'organizationMemberList',
                'organizationRoomAccess', 'spaceAgreementList', 'allocationWindows',
            ],
            // Across slices, as setOrganizationRoomAccess does: the room page and the
            // dialog read a room's organisations through `GroupAccess`, and the week
            // calendar draws the windows from the occupancy read.
            async onQueryStarted(args, api) {
                await evictOccupancy(args, api);
                try {
                    await api.queryFulfilled;
                    api.dispatch(groupAdminApiSlice.util.invalidateTags(['GroupAccess']));
                } catch {
                    // The page shows the error; nothing was written.
                }
            },
        }),
        sendOrganizationImportInvites: builder.mutation<SendOrganizationImportInvitesResult, { inviteIds: string[] }>({
            query: (data) => ({ url: '/organizations/import/send-invites', method: 'POST', data }),
        }),
    }),
});

export const {
    useGetOrganizationsQuery,
    useGetOrganizationQuery,
    useCreateCustomerClassMutation,
    useUpdateCustomerClassMutation,
    useArchiveCustomerClassMutation,
    useCreateOrganizationMutation,
    useUpdateOrganizationMutation,
    useDeleteOrganizationMutation,
    useCreateOrganizationUnitMutation,
    useUpdateOrganizationUnitMutation,
    useDeleteOrganizationUnitMutation,
    useGetOrganizationMembersQuery,
    useGetOrganizationRoomAccessQuery,
    useSetOrganizationRoomAccessMutation,
    useGetOrganizationSpaceAgreementsQuery,
    useCreateSpaceAgreementMutation,
    useUpdateSpaceAgreementMutation,
    useCancelSpaceAgreementMutation,
    useBulkAddOrganizationMembersMutation,
    useRemoveOrganizationMemberMutation,
    useSetMembershipUnitMutation,
    useSetOrganizationPrimaryContactMutation,
    useGetCustomerClassesQuery,
    useGetGrantWindowsQuery,
    usePreviewGrantWindowsMutation,
    useSaveGrantWindowsMutation,
    useReleaseWindowDateMutation,
    useRestoreWindowDateMutation,
    useGetTenantWindowsQuery,
    useLazyGetUtilisationQuery,
    usePreviewCopySeasonMutation,
    useCopySeasonMutation,
    usePreviewPlanImportMutation,
    useImportPlanMutation,
    useGetOrganizationImportFormatQuery,
    usePreviewOrganizationImportMutation,
    useApplyOrganizationImportMutation,
    useSendOrganizationImportInvitesMutation,
} = organizationApiSlice;
